import { useEffect } from 'react';

import { useAuthService } from '@bessemercws/auth';
import { ApolloError, GetEmulationUserProfileTypeQuery, useGetEmulationUserProfileTypeQuery, useGetUserProfileDataQuery } from '@bessemercws/graphql';
import { useClientContext } from '@bessemercws/providers';
import { normalizePath } from '@bessemercws/utils';
import { useLocation, useNavigate } from 'react-router-dom';

import { getHomeRoute } from '#/routes/UserProfileTypeRoutesConfig';

export interface UseUserProfileSetupOptions {
  isAdvisor?: boolean;
  selectedClient?: string | null;
}

export interface UseUserProfileSetupResult {
  isLoading: boolean;
  hasUserIdHash: boolean;
  userProfileType: string | undefined;
  advisorData?: GetEmulationUserProfileTypeQuery;
  advisorError?: ApolloError;
}

/*
TODO: Add this into graphql package for the query used to get user profile type in order to get additional services as well:

query GetUserProfileData {
  viewer {
    userId
    user_profile_type
    user_id_hash
    additionalServices {
      billManagerId
      billPay
      quicken
      hasConsolidatedReporting
      messages
    }
  }
}
In the return down below:

hasAdditionalServices: !!(
  data?.viewer?.additionalServices?.billPay ||
  data?.viewer?.additionalServices?.hasConsolidatedReporting ||
  data?.viewer?.additionalServices?.quicken ||
  data?.viewer?.additionalServices?.messages
),

If this is truthy, the user should be shown the additional services tab in the header. Then, when building the additional services tab, we can check which of the additional services the user has access to and show those in the dropdown.
*/

export const useUserProfileSetup = ({ isAdvisor = false, selectedClient }: UseUserProfileSetupOptions = {}): UseUserProfileSetupResult => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserProfileType, terms_conditions_flag, handleViewerError, idToken } = useClientContext();
  const { getToken, isTokenValid } = useAuthService();

  const isOnLoginPage = normalizePath(location.pathname) === '/login';
  const hasValidToken = !!getToken() && isTokenValid();

  // Client query — skipped in advisor/emulator mode
  const clientSkip = isAdvisor || !hasValidToken || (isOnLoginPage && !terms_conditions_flag);
  const { data: clientData, loading: clientLoading, error: clientError } = useGetUserProfileDataQuery({
    errorPolicy: 'none',
    skip: clientSkip,
  });

  // Emulator query — skipped in client mode or when selectedClient/idToken is absent
  const advisorSkip = !isAdvisor || !selectedClient || !idToken;
  const { data: advisorData, loading: advisorLoading, error: advisorError } = useGetEmulationUserProfileTypeQuery({
    errorPolicy: 'none',
    skip: advisorSkip,
    variables: { userId: selectedClient ?? '' },
  });

  useEffect(() => {
    if (!clientData) return;
    const userProfileTypeResponse = clientData.viewer?.user_profile_type;
    setUserProfileType(userProfileTypeResponse ?? undefined);
    if (isOnLoginPage && terms_conditions_flag) {
      navigate(getHomeRoute(userProfileTypeResponse));
    }
  }, [clientData, isOnLoginPage, navigate, setUserProfileType, terms_conditions_flag]);

  useEffect(() => {
    if (!advisorData) return;
    setUserProfileType(advisorData.advisor?.client.user_profile_type ?? undefined);
  }, [advisorData, setUserProfileType]);

  useEffect(() => {
    if (!clientError) return;
    if (isOnLoginPage) {
      navigate(getHomeRoute());
    }
    handleViewerError(clientError.message ?? 'Unknown error');
  }, [clientError, handleViewerError, isOnLoginPage, navigate]);

  const user = isAdvisor
    ? advisorData?.advisor?.client
    : clientData?.viewer;

  const userProfileType = user?.user_profile_type ?? undefined;

  const hasUserIdHash = Boolean(user?.user_id_hash);

  const isLoading = isAdvisor
    ? !advisorSkip && advisorLoading
    : !clientSkip && clientLoading;

  return {
    isLoading,
    hasUserIdHash,
    userProfileType,
    advisorData,
    advisorError,
  };
};
